import { Component, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import { CorrespondantService } from 'src/app/services/correspondant/correspondant.service';
import { PatientService } from 'src/app/services/patient/patient.service';
import { ReferentielService } from 'src/app/services/referentiel/referentiel.service';
import { StringUtilsService } from 'src/app/services/string-utils/string-utils.service';
import { ReferentielEnum } from 'src/app/utils/enums/referentiel-enum';
import { Adresse } from 'src/app/utils/interfaces/new/adresse.interface';
import { ContactsPatient } from 'src/app/utils/interfaces/new/contacts-patient';
import { CorrespondantPatient } from 'src/app/utils/interfaces/new/correspondant-patient';
import { Intervenant } from 'src/app/utils/interfaces/new/intervenant';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { Repertoire } from 'src/app/utils/interfaces/new/repertoire';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-histo-repertoire',
  templateUrl: './histo-repertoire.fragment.html',
  styleUrls: [
    './histo-repertoire.fragment.scss',
    '../modal.fragment.scss'
  ]
})
export class HistoRepertoireFragment implements OnInit {

  @ViewChild('modalBody') public modalBody: ElementRef;
  @ViewChild('outsideModal') public outsideModal: ElementRef;

  @Output() public closeEmitter = new EventEmitter();

  public idPatient: string;
  public repertoire: ContactsPatient;


  // variables correspondant
  public correspondantColumnTable = [
    {
      title: 'Correspondant', data: 'idCorrespondant',
      isEditable: true, editType: 'ngSelect', editValue: [], validators: [Validators.required], errorMessage: 'Le champs est requis'
    },
    { title: 'Infos complémentaires', data: 'infoComp', isEditable: true, editType: 'string' },
    { title: 'Supprimer', data: null }
  ];
  public correspondantBodyTable: Paginated<CorrespondantPatient> = {
    page: 1,
    perpage: -1,
    objects: [],
    total: 0,
  };
  public correspondantList = [];
  public deletedCorrespondant: CorrespondantPatient[] = [];
  public isLoadingCorrespondant = false;
  public isEditCorrepondant = false;

  // variables intervenant
  public intervenantColumnTable = [
    { title: 'Intervenant', data: 'libelle', isEditable: true, editType: 'string', validators: [Validators.required], errorMessage: 'Le champs est requis' },
    { title: 'Infos complémentaires', data: 'infoComp', isEditable: true, editType: 'string' },
    { title: 'Supprimer', data: null }
  ];
  public intervenantBodyTable: Paginated<Intervenant> = {
    page: 1,
    perpage: -1,
    objects: [],
    total: 0,
  };
  public deletedIntervenant: Intervenant[] = [];
  public isLoadingIntervenant = false;
  public isEditIntervenant = false;

  // variables personne
  public personneColumnTable = [
    { title: 'Personne', data: 'idTypePersonne', mustBeLarge: true, isEditable: true, editType: 'ngSelect', editValue: [], validators: [Validators.required] },
    { title: 'Nom', data: 'nom', isEditable: true, editType: 'string', validators: [Validators.required], errorMessage: 'Le champs est requis' },
    { title: 'Prénom', data: 'prenom', isEditable: true, editType: 'string', validators: [Validators.required], errorMessage: 'Le champs est requis' },
    { title: 'Adresse', data: 'adresse', isEditable: true, editType: 'string' },
    { title: 'Téléphone', data: 'telephone', isEditable: true, editType: 'string', validators: [this.checkPhoneNumber], errorMessage: 'Le numéro saisi est incorrecte' },
    { title: 'Mobile', data: 'telPort', isEditable: true, editType: 'string', validators: [this.checkPhoneNumber], errorMessage: 'Le numéro saisi est incorrecte' },
    { title: 'Email', data: 'email', isEditable: true, editType: 'string', validators: [Validators.email], errorMessage: 'Le mail saisi est incorrecte' },
    { title: 'Infos comp.', data: 'infoComp', isEditable: true, editType: 'string' },
    { title: 'Supprimer', data: null }
  ];
  public personneBodyTable: Paginated<Repertoire> = {
    page: 1,
    perpage: -1,
    objects: [],
    total: 0,
  };
  public personneTypeList = [];
  public deletedPersonne: Repertoire[] = [];
  public isLoadingPersonne = false;
  public isEditPersonne = false;

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event): void {
    if (!environment.closeModalOnClickOutside) {
      return;
    }

    if (!this.modalBody.nativeElement.contains(event.target) && this.outsideModal.nativeElement.contains(event.target)) {
      this.closeHistoRepertoireModal(false);
    }
  }


  constructor(
    private _correspondant: CorrespondantService,
    private _ref: ReferentielService,
    private _patient: PatientService,
    private _strUtilsSrv: StringUtilsService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.initCorrespondant();
    await this.initIntervenant();
    await this.initPersonne();
  }

  async initCorrespondant(): Promise<void> {
    // init correspondant table
    this.isLoadingCorrespondant = true;
    this.repertoire.correspondants = (await this._patient.findOnePatientRepertoire(this.idPatient)).correspondants;
    this.correspondantBodyTable = {
      page: 1,
      perpage: -1,
      objects: this.repertoire.correspondants,
      total: this.repertoire.correspondants.length,
    };
    const correspondants = await this._correspondant.find();
    for (const co of correspondants) {
      this.correspondantList.push({
        id: co.id,
        libelle: (co.titre ? co.titre + ' ' : '') + (co.nom ? co.nom + ' ' : '') + (co.prenom ? this._strUtilsSrv.uppercaseFirstCharString(co.prenom) : ''),
      });
    }
    this.correspondantColumnTable[0].editValue = this.correspondantList;
    this.isLoadingCorrespondant = false;
  }

  async initIntervenant(): Promise<void> {
    // init intervenant table
    this.isLoadingIntervenant = true;
    this.repertoire.intervenants = (await this._patient.findOnePatientRepertoire(this.idPatient)).intervenants;
    this.intervenantBodyTable = {
      page: 1,
      perpage: -1,
      objects: this.repertoire.intervenants,
      total: this.repertoire.intervenants.length,
    };
    this.isLoadingIntervenant = false;
  }

  async initPersonne(): Promise<void> {
    // init personne table
    this.isLoadingPersonne = true;
    this.repertoire.repertoires = (await this._patient.findOnePatientRepertoire(this.idPatient)).repertoires;
    this.personneBodyTable = {
      page: 1,
      perpage: -1,
      objects: this.repertoire.repertoires,
      total: this.repertoire.repertoires.length,
    };
    this.personneTypeList = await this._ref.findRefListTable(ReferentielEnum.typePersonne);
    this.personneColumnTable[0].editValue = this.personneTypeList;
    this.isLoadingPersonne = false;
  }

  async deleteCorrespondant(event: any): Promise<void> {
    const deleteCorrepondantIndex = this.repertoire.correspondants.findIndex(x => x.id === event.id);
    if (deleteCorrepondantIndex > -1) {
      this.repertoire.correspondants[deleteCorrepondantIndex].disabled = true;
      await this._patient.updatePatientRepertoire(this.idPatient, this.repertoire);
      await this.initCorrespondant();
    }
  }

  async deleteIntervenant(event: any): Promise<void> {
    const deleteIntervenantIndex = this.repertoire.intervenants.findIndex(x => x.id === event.id);
    if (deleteIntervenantIndex > -1) {
      this.repertoire.intervenants[deleteIntervenantIndex].disabled = true;
      await this._patient.updatePatientRepertoire(this.idPatient, this.repertoire);
      await this.initIntervenant();
    }
  }

  async deletePersonne(event: any): Promise<void> {
    const deletePersonneIndex = this.repertoire.repertoires.findIndex(x => x.id === event.id);
    if (deletePersonneIndex > -1) {
      this.repertoire.repertoires[deletePersonneIndex].disabled = true;
      await this._patient.updatePatientRepertoire(this.idPatient, this.repertoire);
      await this.initPersonne();
    }
  }

  async changeCorrespondant(event: any): Promise<void> {
    this.isEditCorrepondant = false;
    for (const correspondant of event) {
      const index = this.repertoire.correspondants.findIndex(x => x.id === correspondant.id);
      if (index > -1) {
        this.repertoire.correspondants[index] = {
          id: correspondant.id,
          idCorrespondant: correspondant.idCorrespondant,
          idPatient: this.idPatient,
          infoComp: correspondant.infoComp,
          disabled: false,
        };
      } else {
        this.repertoire.correspondants.push({
          idCorrespondant: correspondant.idCorrespondant,
          idPatient: this.idPatient,
          infoComp: correspondant.infoComp,
          disabled: false,
        });
      }
    }
    await this._patient.updatePatientRepertoire(this.idPatient, this.repertoire);
    await this.initCorrespondant();
  }

  async changeIntervenant(event: any): Promise<void> {
    this.isEditIntervenant = false;
    for (const interventant of event) {
      const index = this.repertoire.intervenants.findIndex(x => x.id === interventant.id);
      if (index > -1) {
        this.repertoire.intervenants[index] = {
          id: interventant.id,
          idPatient: this.idPatient,
          libelle: interventant.libelle,
          disabled: false,
          infoComp: interventant.infoComp,
        };
      } else {
        this.repertoire.intervenants.push({
          idPatient: this.idPatient,
          libelle: interventant.libelle,
          disabled: false,
          infoComp: interventant.infoComp,
        });
      }
    }
    await this._patient.updatePatientRepertoire(this.idPatient, this.repertoire);
    await this.initIntervenant();
  }

  async changePersonne(event: any): Promise<void> {
    this.isEditPersonne = false;
    for (const personne of event) {
      const index = this.repertoire.repertoires.findIndex(x => x.id === personne.id);
      if (index > -1) {
        this.personneBodyTable.objects[index] = {
          id: personne.id,
          idPatient: this.idPatient,
          idTypePersonne: personne.idTypePersonne,
          nom: personne.nom.toLocaleUpperCase(),
          prenom: this._strUtilsSrv.uppercaseFirstCharString(personne.prenom),
          adresse: personne.adresse,
          telephone: personne.telephone,
          telPort: personne.telPort,
          email: personne.email,
          infoComp: personne.infoComp,
          disabled: false,
        };
      } else {
        this.repertoire.repertoires.push({
          idPatient: this.idPatient,
          idTypePersonne: personne.idTypePersonne,
          nom: personne.nom.toLocaleUpperCase(),
          prenom: this._strUtilsSrv.uppercaseFirstCharString(personne.prenom),
          adresse: personne.adresse,
          telephone: personne.telephone,
          telPort: personne.telPort,
          email: personne.email,
          infoComp: personne.infoComp,
          disabled: false,
        });
      }
    }
    await this._patient.updatePatientRepertoire(this.idPatient, this.repertoire);
    await this.initPersonne();
  }

  public closeHistoRepertoireModal(emit: boolean): void {
    if (emit) {
      this.closeEmitter.emit(this.repertoire);
    } else {
      this.closeEmitter.emit();
    }
  }

  public checkPhoneNumber(control: AbstractControl): { [key: string]: boolean } | null {
    const regexPhone = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/gmi;
    if (control.value !== undefined && control.value !== '' && !regexPhone.test(control.value)) {
      return { 'patternError': true };
    }
    return null;
  }

}
